import { api } from './client';

export const payrollApi = {
  // ========== 임금대장 / 급여명세서 ==========
  getPayrollRecords: async (payYear?: number, payMonth?: number, employeeId?: string, team?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (payYear) params.append('pay_year', String(payYear));
    if (payMonth) params.append('pay_month', String(payMonth));
    if (employeeId) params.append('employee_id', employeeId);
    if (team) params.append('team', team);
    const response = await api.get(`/api/payroll/records?${params.toString()}`);
    return response.data;
  },

  createPayrollRecord: async (data: any): Promise<any> => {
    const response = await api.post('/api/payroll/records', data);
    return response.data;
  },

  updatePayrollRecord: async (recordId: number, data: any): Promise<any> => {
    const response = await api.put(`/api/payroll/records/${recordId}`, data);
    return response.data;
  },

  deletePayrollRecord: async (recordId: number): Promise<any> => {
    const response = await api.delete(`/api/payroll/records/${recordId}`);
    return response.data;
  },

  getPaySlips: async (payYear?: number, payMonth?: number, status?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (payYear) params.append('pay_year', String(payYear));
    if (payMonth) params.append('pay_month', String(payMonth));
    if (status) params.append('status', status);
    const response = await api.get(`/api/payroll/slips?${params.toString()}`);
    return response.data;
  },

  getMyPaySlips: async (payYear?: number): Promise<any> => {
    const params = payYear ? `?pay_year=${payYear}` : '';
    const response = await api.get(`/api/payroll/slips/my${params}`);
    return response.data;
  },

  issuePaySlips: async (payYear: number, payMonth: number): Promise<any> => {
    const response = await api.post(`/api/payroll/slips/issue?pay_year=${payYear}&pay_month=${payMonth}`);
    return response.data;
  },

  acknowledgePaySlip: async (slipId: number): Promise<any> => {
    const response = await api.post(`/api/payroll/slips/${slipId}/acknowledge`);
    return response.data;
  },

  getPayrollSummary: async (payYear: number, payMonth?: number): Promise<any> => {
    const params = new URLSearchParams({ pay_year: String(payYear) });
    if (payMonth) params.append('pay_month', String(payMonth));
    const response = await api.get(`/api/payroll/summary?${params.toString()}`);
    return response.data;
  },

  // === 연봉 체계 (BACK-073) ===
  getSalaryTables: async (companyId?: number): Promise<any> => {
    const response = await api.get('/api/salary/tables', { params: { company_id: companyId || 1 } });
    return response.data;
  },

  updateSalaryTable: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/salary/tables/${id}`, data);
    return response.data;
  },

  getSalaryHistory: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/salary/history/${employeeId}`);
    return response.data;
  },

  addSalaryRecord: async (data: any): Promise<any> => {
    const response = await api.post('/api/salary/records', data);
    return response.data;
  },

  simulateSalaryIncrease: async (increaseRate: number, companyId?: number): Promise<any> => {
    const response = await api.post('/api/salary/simulate', { increase_rate: increaseRate }, { params: { company_id: companyId || 1 } });
    return response.data;
  },

  getSalaryStats: async (companyId?: number): Promise<any> => {
    const response = await api.get('/api/salary/stats', { params: { company_id: companyId || 1 } });
    return response.data;
  },

  // === 인센티브 시뮬레이터 (BACK-129) ===
  createIncentivePool: async (data: any): Promise<any> => {
    const response = await api.post('/api/incentive/pools', data);
    return response.data;
  },

  getIncentivePools: async (year?: number): Promise<any> => {
    const params: any = {};
    if (year) params.year = year;
    const response = await api.get('/api/incentive/pools', { params });
    return response.data;
  },

  getIncentivePool: async (id: number): Promise<any> => {
    const response = await api.get(`/api/incentive/pools/${id}`);
    return response.data;
  },

  simulateIncentive: async (totalAmount: number, companyId?: number): Promise<any> => {
    const response = await api.post('/api/incentive/simulate', { total_amount: totalAmount, company_id: companyId || 1 });
    return response.data;
  },

  simulateGroupIncentive: async (totalAmount: number, companies: any[]): Promise<any> => {
    const response = await api.post('/api/incentive/simulate-group', { total_amount: totalAmount, companies });
    return response.data;
  },

  saveIncentiveSimulation: async (poolId: number, distributions: any[]): Promise<any> => {
    const response = await api.post('/api/incentive/save', { pool_id: poolId, distributions });
    return response.data;
  },

  // === 인건비 효율성 분석 ==========
  getLaborCostSummary: async (year: number, month?: number): Promise<any> => {
    const params = new URLSearchParams({ year: String(year) });
    if (month) params.append('month', String(month));
    const response = await api.get(`/api/labor-cost/summary?${params}`);
    return response.data;
  },

  getLaborCostByTeam: async (year: number, month?: number): Promise<any> => {
    const params = new URLSearchParams({ year: String(year) });
    if (month) params.append('month', String(month));
    const response = await api.get(`/api/labor-cost/by-team?${params}`);
    return response.data;
  },

  getLaborCostByRank: async (year: number, month?: number): Promise<any> => {
    const params = new URLSearchParams({ year: String(year) });
    if (month) params.append('month', String(month));
    const response = await api.get(`/api/labor-cost/by-rank?${params}`);
    return response.data;
  },

  getLaborCostTrend: async (year: number): Promise<any> => {
    const response = await api.get(`/api/labor-cost/trend?year=${year}`);
    return response.data;
  },

  simulateLaborCost: async (data: { year: number; raise_rate: number; target_team?: string; target_rank_level?: number }): Promise<any> => {
    const response = await api.post('/api/labor-cost/simulate', data);
    return response.data;
  },

  // === 퇴직금 관리 ===
  getSeveranceAlerts: async (): Promise<any> => {
    const response = await api.get('/api/severance/alerts');
    return response.data;
  },

  getAllSeverances: async (status?: string): Promise<any> => {
    const response = await api.get('/api/severance', { params: status ? { status } : {} });
    return response.data;
  },

  calculateSeverance: async (data: { employee_id: string; termination_date: string; monthly_wages: number[]; hire_date?: string }): Promise<any> => {
    const response = await api.post('/api/severance/calculate', data);
    return response.data;
  },

  // === 직급 매핑 ==========
  getGroupLevels: async (): Promise<any> => {
    const response = await api.get('/api/rank-mapping/levels');
    return response.data;
  },

  createGroupLevel: async (data: any): Promise<any> => {
    const response = await api.post('/api/rank-mapping/levels', data);
    return response.data;
  },

  getRankMappings: async (companyId: number): Promise<any> => {
    const response = await api.get(`/api/rank-mapping/companies/${companyId}`);
    return response.data;
  },

  setRankMappings: async (companyId: number, mappings: Array<{ rank_id: number; group_level_id: number }>): Promise<any> => {
    const response = await api.put(`/api/rank-mapping/companies/${companyId}`, mappings);
    return response.data;
  },

  getSalaryBands: async (companyId: number): Promise<any> => {
    const response = await api.get(`/api/rank-mapping/salary-bands/${companyId}`);
    return response.data;
  },

  setSalaryBands: async (companyId: number, bands: Array<{ group_level_id: number; min_salary: number; max_salary: number; base_salary: number }>): Promise<any> => {
    const response = await api.put(`/api/rank-mapping/salary-bands/${companyId}`, bands);
    return response.data;
  },

  simulateTransferRank: async (employeeId: string, toCompanyId: number): Promise<any> => {
    const response = await api.get(`/api/rank-mapping/simulate/${employeeId}`, { params: { to_company_id: toCompanyId } });
    return response.data;
  },

  getRankMappingSummary: async (): Promise<any> => {
    const response = await api.get('/api/rank-mapping/summary');
    return response.data;
  },

  // === 승진 관리 ==========
  getPromotionRules: async (): Promise<any> => {
    const response = await api.get('/api/promotions/rules');
    return response.data;
  },

  createPromotionRule: async (data: { from_rank_id: number; to_rank_id: number; min_tenure_years: number; min_eval_grade: string; min_eval_count: number; required_training_ids?: number[] }): Promise<any> => {
    const response = await api.post('/api/promotions/rules', data);
    return response.data;
  },

  updatePromotionRule: async (ruleId: number, data: { min_tenure_years?: number; min_eval_grade?: string; min_eval_count?: number; required_training_ids?: number[] }): Promise<any> => {
    const response = await api.put(`/api/promotions/rules/${ruleId}`, data);
    return response.data;
  },

  extractPromotionCandidates: async (): Promise<any> => {
    const response = await api.post('/api/promotions/extract-candidates');
    return response.data;
  },

  getPromotionCandidates: async (status?: string, fromRankId?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (fromRankId) params.append('from_rank_id', String(fromRankId));
    const response = await api.get(`/api/promotions/candidates?${params.toString()}`);
    return response.data;
  },

  updatePromotionCandidate: async (candidateId: number, status: string, notes?: string): Promise<any> => {
    const response = await api.patch(`/api/promotions/candidates/${candidateId}`, { status, notes });
    return response.data;
  },

  executePromotion: async (candidateId: number): Promise<any> => {
    const response = await api.post(`/api/promotions/candidates/${candidateId}/execute`);
    return response.data;
  },

  getPromotionStatistics: async (): Promise<any> => {
    const response = await api.get('/api/promotions/statistics');
    return response.data;
  },
};
